import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';

const getBaseUrl = () => {
  const base = API_BASE_URL;
  return base.endsWith('/api') ? base : `${base}/api`;
};

export interface BundleComponent {
  id: number;
  component_product: number;
  component_product_name: string;
  component_product_code: string;
  quantity_per_bundle: number;
}

export interface BundleDefinition {
  id: number;
  bundle_product: number;
  bundle_product_name: string;
  bundle_product_code: string;
  bundle_product_quantity: number;
  is_active: boolean;
  shelf_life_days: number | null;
  components: BundleComponent[];
  active_instances_count: number;
  total_assembled: number;
  warehouse?: number | null;
  created_at: string;
  updated_at: string;
  created_by: number | null;
}

export interface BundleInstance {
  id: number;
  definition: number;
  definition_name: string;
  quantity: number;
  document: number | null;
  document_number: string | null;
  storage_location: number | null;
  storage_location_code: string | null;
  created_at: string;
  created_by: number | null;
  created_by_name: string | null;
  is_disassembled: boolean;
  disassembled_at: string | null;
  disassembled_by: number | null;
  disassembled_by_name: string | null;
}

export const bundlesApi = {
  getDefinitions: async (params?: { active_only?: boolean; product_id?: number; warehouse?: number }): Promise<BundleDefinition[]> => {
    const base = getBaseUrl();
    const response = await axios.get(`${base}/bundle-definitions/`, { params });
    return response.data.results || response.data;
  },

  getDefinition: async (id: number): Promise<BundleDefinition> => {
    const base = getBaseUrl();
    const response = await axios.get(`${base}/bundle-definitions/${id}/`);
    return response.data;
  },

  createDefinition: async (data: {
    bundle_product_id: number;
    components: Array<{ component_product_id: number; quantity_per_bundle: number }>;
    warehouse_id?: number;
    shelf_life_days?: number | null;
  }): Promise<BundleDefinition> => {
    const base = getBaseUrl();
    const response = await axios.post(`${base}/bundle-definitions/`, data);
    return response.data;
  },

  createWithProduct: async (data: {
    name: string;
    product_code: string;
    price?: number;
    description?: string;
    components: Array<{ component_product_id: number; quantity_per_bundle: number }>;
    warehouse_id?: number;
    shelf_life_days?: number | null;
  }): Promise<BundleDefinition> => {
    const base = getBaseUrl();
    const response = await axios.post(`${base}/bundle-definitions/create-with-product/`, data);
    return response.data;
  },

  assemble: async (data: {
    bundle_definition_id: number;
    quantity: number;
    storage_location_id?: number;
    source_locations?: Array<{ component_product_id: number; storage_location_id: number }>;
  }): Promise<{ instance: BundleInstance; document_number: string; document_id: number }> => {
    const base = getBaseUrl();
    const response = await axios.post(`${base}/bundle-definitions/assemble/`, data);
    return response.data;
  },

  disassemble: async (data: {
    bundle_instance_id: number;
    target_storage_location_id?: number;
  }): Promise<{ instance: BundleInstance; document_number: string; document_id: number }> => {
    const base = getBaseUrl();
    const response = await axios.post(`${base}/bundle-definitions/disassemble/`, data);
    return response.data;
  },

  getInstances: async (params?: {
    definition_id?: number;
    active_only?: boolean;
    product_id?: number;
  }): Promise<BundleInstance[]> => {
    const base = getBaseUrl();
    const response = await axios.get(`${base}/bundle-instances/`, { params });
    return response.data.results || response.data;
  },
};
